import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Link
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import WarningIcon from '@mui/icons-material/Warning';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import api from '../../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const NotificacaoMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificacoes, setNotificacoes] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);

  const formatDate = (date) => {
    return format(new Date(date), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR });
  };

  const buscarNotificacoes = async () => {
    try {
      const response = await api.get('/notificacoes');
      setNotificacoes(response.data);
      setNaoLidas(response.data.filter(n => !n.lida).length);
    } catch (error) {
      setNotificacoes([]);
      setNaoLidas(0);
    }
  };

  useEffect(() => {
    buscarNotificacoes();
    const interval = setInterval(buscarNotificacoes, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const marcarComoLida = async (id) => {
    try {
      await api.put(`/notificacoes/${id}/lida`);
      buscarNotificacoes();
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'cobranca':
        return <MoneyOffIcon fontSize="small" color="error" />;
      case 'parcela_atrasada':
        return <WarningIcon fontSize="small" color="error" />;
      case 'vencimento_proximo':
        return <WarningIcon fontSize="small" color="warning" />;
      case 'sms':
        return <NotificationsIcon fontSize="small" color="info" />;
      default:
        return null;
    }
  };

  const getTipoChip = (tipo) => {
    const config = {
      cobranca: { label: 'Cobrança', color: 'error' },
      parcela_atrasada: { label: 'Parcela Atrasada', color: 'error' },
      vencimento_proximo: { label: 'Vencimento Próximo', color: 'warning' },
      sms: { label: 'SMS', color: 'info' }
    };
    const tipoConfig = config[tipo] || { label: tipo, color: 'default' };
    
    return (
      <Chip
        size="small"
        label={tipoConfig.label}
        color={tipoConfig.color}
        sx={{ ml: 1 }}
      />
    );
  };

  return (
    <>
      <IconButton
        size="large"
        aria-label="show notifications"
        color="inherit"
        onClick={handleClick}
      >
        <Badge badgeContent={naoLidas} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: '400px',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notificações</Typography>
          <Chip label={`${naoLidas} não lidas`} size="small" color="primary" />
        </Box>
        <Divider />
        <List sx={{ p: 0, maxHeight: 300, overflowY: 'auto' }}>
          {notificacoes.length === 0 ? (
            <ListItem>
              <ListItemText primary="Nenhuma notificação" />
            </ListItem>
          ) : (
            notificacoes.map((notificacao) => (
              <MenuItem
                key={notificacao.id}
                onClick={() => marcarComoLida(notificacao.id)}
                sx={{
                  backgroundColor: notificacao.lida ? 'inherit' : '#f0f7ff',
                  '&:hover': {
                    backgroundColor: notificacao.lida ? '#f5f5f5' : '#e3f2fd',
                  },
                  display: 'block',
                  py: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  {getTipoIcon(notificacao.tipo)}
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: notificacao.lida ? 'normal' : 'bold' }}>
                    {notificacao.mensagem}
                  </Typography>
                  {getTipoChip(notificacao.tipo)}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                  {formatDate(notificacao.data_criacao)}
                </Typography>
              </MenuItem>
            ))
          )}
        </List>
        <Divider />
        <MenuItem component={Link} to="/notificacoes/historico">
          Histórico
        </MenuItem>
        <MenuItem component={Link} to="/notificacoes/configuracoes">
          Configurações
        </MenuItem>
      </Menu>
    </>
  );
};

export default NotificacaoMenu;